/** Every account, and the two things an admin may do to one.
 *
 * The spec gives this screen a short brief — list, identities, token count,
 * source ids, upload count; disable an account — and the brevity is the
 * point. There is no editing here: an account's name is the account's to
 * change, its tokens are the account's to revoke, and its source ids never
 * move except through a `source_conflict` issue that a person resolves with
 * the evidence in front of them. What an admin can do is stop an account
 * sending anything further, which is what "disable" means and all it means.
 *
 * So disabling is asked twice, with the consequence written out: uploads are
 * refused from now on, everything already merged stays, because other
 * people's surveys are corroborated against it. The note goes to the audit
 * trail, not to the account, and the service refuses to disable the last
 * enabled administrator rather than let a deployment lock itself out. That
 * refusal is shown where the decision was made, inside the dialog.
 *
 * Enabling is one click. It undoes an admin's decision, not a person's.
 *
 * The second thing is the pace. The server takes one upload of a circuit per
 * `types.tracks.min_interval_s`, and caps what one token or installation may
 * send across all circuits inside a window, which is sized for an honest
 * backlog and will still stop somebody with a larger one. The column says who
 * is being refused today, the filter finds them, and an account can be given
 * an interval of its own — "no wait" lifts the cap for it as well. That is an
 * allowance, not a state, and taking it back is one click for the same reason
 * enabling is: it undoes an admin's decision. The server's own numbers, for
 * everybody, are settings and live in Settings.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminUsers, disableUser, setUploadInterval } from '@/lib/api';
import { bytes, count, DASH, dateTime, relativeTime, shortDate } from '@/lib/format';
import type {
  AccountPaceFilter,
  AccountState,
  AdminAccount,
  AdminAccountPage,
  Role,
} from '@/lib/types';
import {
  Avatar,
  EmptyState,
  ErrorNote,
  LoadingPanel,
  Page,
  Segmented,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tag,
  TextField,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/profile/ConfirmDialog';
import {
  ACCOUNT_STATES,
  AdminHeader,
  LoadMore,
  Notice,
  accountState,
  accountStateTone,
  identityLabel,
  intervalLabel,
  isAccountState,
  isPaceFilter,
  parseInterval,
  usePaged,
  useQueryParams,
} from '@/components/admin';

type StateFilter = AccountState | 'all';
type RoleFilter = Role | 'all';
type PaceFilter = AccountPaceFilter | 'all';

/** Intervals worth a button: no wait, and two that still leave a limit. */
const PACE_PRESETS = [0, 5, 15] as const;

/** Until a page has said otherwise: the server's shipped interval and cap. */
const PACE_FALLBACK = {
  server_interval_s: 60,
  burst_uploads: 30,
  burst_window_s: 600,
  max_interval_s: 86400,
  limited_window_hours: 24,
};

const MONO_SMALL = {
  display: 'block',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  color: 'var(--color-neutral-600)',
} as const;

const MUTED = { color: 'var(--color-neutral-500)' } as const;

export default function AdminUsers() {
  const [search, setParam] = useQueryParams();
  const q = search.get('q') ?? '';
  const rawState = search.get('state') ?? 'all';
  const state: StateFilter = isAccountState(rawState) ? rawState : 'all';
  const rawRole = search.get('role') ?? 'all';
  const role: RoleFilter = rawRole === 'admin' || rawRole === 'user' ? rawRole : 'all';
  const rawPace = search.get('pace') ?? 'all';
  const pace: PaceFilter = isPaceFilter(rawPace) ? rawPace : 'all';
  const narrowed = Boolean(q) || state !== 'all' || role !== 'all' || pace !== 'all';

  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);

  /** What "default" is today, and how far back "refused" looks: the page's. */
  const [serverPace, setServerPace] = useState(PACE_FALLBACK);

  const fetcher = useCallback(
    (cursor: string | undefined) =>
      adminUsers({
        q: q || undefined,
        state: state === 'all' ? undefined : state,
        role: role === 'all' ? undefined : role,
        pace: pace === 'all' ? undefined : pace,
        cursor,
        limit: 50,
      }).then((page) => {
        setServerPace(page.upload_pace);
        return page;
      }),
    [q, state, role, pace],
  );
  const listing = usePaged<AdminAccount, AdminAccountPage>(fetcher);

  /** The account the disable dialog is open for. */
  const [target, setTarget] = useState<AdminAccount | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<unknown>(null);
  const [rowError, setRowError] = useState<unknown>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);

  /** The account the pace dialog is open for, and what has been typed into it. */
  const [paceTarget, setPaceTarget] = useState<AdminAccount | null>(null);
  const [paceDraft, setPaceDraft] = useState('');
  const [paceReason, setPaceReason] = useState('');
  const [paceError, setPaceError] = useState<unknown>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setDisabled = async (account: AdminAccount, disabled: boolean, note: string) => {
    setBusy(true);
    setRowError(null);
    setDialogError(null);
    try {
      const changed = await disableUser(account.id, note ? { disabled, reason: note } : { disabled });
      if (!alive.current) return;
      listing.patch((items) =>
        items.map((row) =>
          row.id === changed.id
            ? { ...row, disabled: changed.disabled, disabled_at: changed.disabled_at }
            : row,
        ),
      );
      const word = changed.disabled ? 'disabled' : 'enabled';
      setNotice(
        changed.changed
          ? { tone: 'ok', text: `${account.display_name} ${word}` }
          : { tone: 'warn', text: `${account.display_name} was already ${word}; nothing was written` },
      );
      setTarget(null);
      setReason('');
    } catch (thrown) {
      if (!alive.current) return;
      // The dialog is still open for a disable; the row is all there is for
      // an enable.
      if (disabled) setDialogError(thrown);
      else setRowError(thrown);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const setPace = async (account: AdminAccount, interval: number | null, note: string) => {
    setBusy(true);
    setRowError(null);
    setPaceError(null);
    try {
      const changed = await setUploadInterval(
        account.id,
        note ? { interval_s: interval, reason: note } : { interval_s: interval },
      );
      if (!alive.current) return;
      setServerPace((current) => ({ ...current, server_interval_s: changed.server_interval_s }));
      listing.patch((items) =>
        items.map((row) =>
          row.id === changed.id
            ? {
                ...row,
                upload_pace: {
                  ...row.upload_pace,
                  own_interval_s: changed.own_interval_s,
                  interval_s: changed.interval_s,
                },
              }
            : row,
        ),
      );
      const held = intervalLabel(changed.interval_s);
      setNotice(
        !changed.changed
          ? { tone: 'warn', text: `${account.display_name} was already held to ${held}; nothing was written` }
          : changed.own_interval_s === null
            ? { tone: 'ok', text: `${account.display_name} is back on the server’s interval, ${held}` }
            : { tone: 'ok', text: `${account.display_name} now uploads at ${held} — put it back when the backlog is through` },
      );
      setPaceTarget(null);
      setPaceDraft('');
      setPaceReason('');
    } catch (thrown) {
      if (!alive.current) return;
      // The dialog when it is open, the row for the one-click way back.
      if (paceTarget) setPaceError(thrown);
      else setRowError(thrown);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const paceValue = parseInterval(paceDraft, serverPace.max_interval_s);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setParam({ q: draft.trim() });
  };

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader />

      <section aria-label="Accounts" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <Segmented<StateFilter>
            label="filter by state"
            value={state}
            options={[
              { value: 'all', label: 'All' },
              ...ACCOUNT_STATES.map((value) => ({ value, label: value })),
            ]}
            onChange={(next) => setParam({ state: next === 'all' ? null : next })}
          />
          <Segmented<RoleFilter>
            label="filter by role"
            value={role}
            options={[
              { value: 'all', label: 'Any role' },
              { value: 'admin', label: 'Admins' },
              { value: 'user', label: 'Users' },
            ]}
            onChange={(next) => setParam({ role: next === 'all' ? null : next })}
          />
          <Segmented<PaceFilter>
            label="filter by upload pace"
            value={pace}
            options={[
              { value: 'all', label: 'Any pace' },
              { value: 'limited', label: 'Rate limited' },
              { value: 'own', label: 'Own interval' },
            ]}
            onChange={(next) => setParam({ pace: next === 'all' ? null : next })}
          />

          <form
            onSubmit={submitSearch}
            style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginLeft: 'auto' }}
          >
            <TextField
              label="search"
              placeholder="display name, or an account id"
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
              style={{ minHeight: 30, fontSize: 12.5, width: 240 }}
            />
            <button type="submit" className="btn btn-secondary" style={{ fontSize: 12 }}>
              Search
            </button>
            {q ? (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
                onClick={() => setParam({ q: null })}
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>

        {notice ? (
          <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
            {notice.text}
          </Notice>
        ) : null}
        {rowError ? <ErrorNote error={rowError} title="the account was not changed" compact /> : null}
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-neutral-600)' }}>
          The server takes one upload of a circuit per{' '}
          <strong style={{ color: 'inherit' }}>{intervalLabel(serverPace.server_interval_s)}</strong>
          {serverPace.burst_uploads > 0 ? (
            <>
              , and at most{' '}
              <strong style={{ color: 'inherit' }}>
                {count(serverPace.burst_uploads)} uploads per {Math.round(serverPace.burst_window_s / 60)} min
              </strong>{' '}
              across all circuits
            </>
          ) : null}
          . “Rate limited” is the accounts refused by either in the last{' '}
          {serverPace.limited_window_hours} h. To change them for everybody,{' '}
          <Link to="/admin/settings">Settings → Limits</Link>.
        </p>
        {listing.error ? (
          <ErrorNote error={listing.error} title="the accounts could not be read" onRetry={listing.reload} />
        ) : null}

        {listing.loading ? (
          <LoadingPanel label="loading accounts" />
        ) : listing.items.length === 0 ? (
          <EmptyState title={narrowed ? 'no account matches' : 'no accounts yet'}>
            {narrowed
              ? pace === 'limited'
                ? `Nobody has been refused by the upload limit in the last ${serverPace.limited_window_hours} h.`
                : 'Clear a filter to widen the search.'
              : 'The first person to sign in becomes the administrator.'}
          </EmptyState>
        ) : (
          <Table scroll label="Accounts" style={{ fontSize: 13 }}>
            <THead>
              <TR>
                <TH>Account</TH>
                <TH>Sign-in</TH>
                <TH numeric title="live tokens / ever issued">Tokens</TH>
                <TH numeric>Installations</TH>
                <TH numeric title="merged / sent">Uploads</TH>
                <TH numeric>Sessions</TH>
                <TH title={`the wait between track uploads, and 429s in the last ${serverPace.limited_window_hours} h`}>
                  Upload pace
                </TH>
                <TH>Joined</TH>
                <TH>State</TH>
                <TH>
                  <span className="sr-only">actions</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {listing.items.map((account) => {
                const current = accountState(account);
                const installations = account.sources
                  .map((source) => `${source.label || source.source_id} (${count(source.uploads)})`)
                  .join(', ');
                return (
                  <TR key={account.id} style={current === 'deleted' ? { opacity: 0.55 } : undefined}>
                    <TD nowrap>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={account.display_name} size={24} />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {account.display_name}
                            {account.role === 'admin' ? <Tag tone="outline">admin</Tag> : null}
                          </span>
                          <span style={MONO_SMALL}>{account.id}</span>
                        </span>
                      </span>
                    </TD>
                    <TD>
                      {account.identities.length === 0 ? (
                        <span style={MUTED}>{DASH}</span>
                      ) : (
                        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {account.identities.map((identity) => (
                            <Tag
                              key={`${identity.provider}:${identity.provider_login}`}
                              tone="neutral"
                              title={`since ${shortDate(identity.created_at)}`}
                            >
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                                {identityLabel(identity)}
                              </span>
                            </Tag>
                          ))}
                        </span>
                      )}
                    </TD>
                    <TD numeric>
                      {count(account.tokens.active)}{' '}
                      <span style={MUTED}>/ {count(account.tokens.total)}</span>
                    </TD>
                    <TD numeric title={installations || undefined}>
                      {count(account.sources.length)}
                    </TD>
                    <TD numeric>
                      <Link
                        to={`/admin/uploads?user_id=${encodeURIComponent(account.id)}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                        title="open this account’s uploads"
                      >
                        {count(account.uploads.merged)}{' '}
                        <span style={MUTED}>/ {count(account.uploads.total)}</span>
                      </Link>
                    </TD>
                    <TD numeric>
                      <Link
                        to={`/admin/sessions?user_id=${encodeURIComponent(account.id)}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                        title="open this account’s sessions"
                      >
                        {count(account.sessions.total)}
                      </Link>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--color-neutral-600)' }}>
                        {account.sessions.bytes > 0 ? bytes(account.sessions.bytes) : DASH}
                      </span>
                    </TD>
                    <TD nowrap>
                      {account.upload_pace.own_interval_s === null ? (
                        <span style={MUTED}>{intervalLabel(account.upload_pace.interval_s)}</span>
                      ) : (
                        <Tag tone="warn" title={`the server’s is ${intervalLabel(serverPace.server_interval_s)}`}>
                          own · {intervalLabel(account.upload_pace.own_interval_s)}
                        </Tag>
                      )}
                      {account.upload_pace.limited > 0 ? (
                        <span
                          style={{ display: 'block', fontSize: 11, color: 'var(--color-warn, var(--color-neutral-600))' }}
                          title={account.upload_pace.last_limited_at ? dateTime(account.upload_pace.last_limited_at) : undefined}
                        >
                          refused {count(account.upload_pace.limited)}× · last{' '}
                          {relativeTime(account.upload_pace.last_limited_at)}
                        </span>
                      ) : null}
                    </TD>
                    <TD nowrap muted title={dateTime(account.created_at)}>
                      {shortDate(account.created_at)}
                    </TD>
                    <TD nowrap>
                      <Tag tone={accountStateTone(current)}>{current}</Tag>
                      {current === 'disabled' && account.disabled_at ? (
                        <span
                          style={{ display: 'block', fontSize: 11, color: 'var(--color-neutral-600)' }}
                          title={dateTime(account.disabled_at)}
                        >
                          {relativeTime(account.disabled_at)}
                        </span>
                      ) : null}
                      {current === 'deleted' && account.deleted_at ? (
                        <span
                          style={{ display: 'block', fontSize: 11, color: 'var(--color-neutral-600)' }}
                          title={dateTime(account.deleted_at)}
                        >
                          {relativeTime(account.deleted_at)}
                        </span>
                      ) : null}
                    </TD>
                    <TD nowrap align="right">
                      {current === 'deleted' ? null : (
                        <span style={{ marginRight: 6 }}>
                          {account.upload_pace.own_interval_s !== null ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ fontSize: 11.5, marginRight: 6 }}
                              disabled={busy}
                              title={`back to the server’s ${intervalLabel(serverPace.server_interval_s)}`}
                              onClick={() => void setPace(account, null, '')}
                            >
                              Default pace
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: 11.5 }}
                            disabled={busy}
                            onClick={() => {
                              setPaceTarget(account);
                              setPaceDraft(
                                account.upload_pace.own_interval_s === null
                                  ? ''
                                  : String(account.upload_pace.own_interval_s),
                              );
                              setPaceReason('');
                              setPaceError(null);
                            }}
                          >
                            Pace…
                          </button>
                        </span>
                      )}
                      {current === 'deleted' ? (
                        <span style={MUTED}>{DASH}</span>
                      ) : current === 'disabled' ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 11.5 }}
                          disabled={busy}
                          onClick={() => void setDisabled(account, false, '')}
                        >
                          Enable
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-danger"
                          style={{ fontSize: 11.5 }}
                          disabled={busy}
                          onClick={() => {
                            setTarget(account);
                            setReason('');
                            setDialogError(null);
                          }}
                        >
                          Disable…
                        </button>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}

        <LoadMore listing={listing} />
      </section>

      {target ? (
        <ConfirmDialog
          title={`Disable ${target.display_name}?`}
          confirmLabel="Disable"
          tone="danger"
          busy={busy}
          error={dialogError}
          onCancel={() => {
            if (busy) return;
            setTarget(null);
            setDialogError(null);
          }}
          onConfirm={() => void setDisabled(target, true, reason.trim())}
        >
          <p style={{ margin: 0 }}>
            Every upload from this account is refused from now on, with 403, until it is enabled
            again. Everything it has already merged stays — other people’s surveys are corroborated
            against it — and its tokens are not revoked.
          </p>
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="disable-reason">
              note{' '}
              <span style={{ color: 'var(--color-neutral-600)' }}>
                — to the audit trail, not to the account
              </span>
            </label>
            <textarea
              id="disable-reason"
              className="input"
              rows={2}
              value={reason}
              style={{ minHeight: 52, fontSize: 13 }}
              placeholder="why"
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </ConfirmDialog>
      ) : null}

      {paceTarget ? (
        <ConfirmDialog
          title={`Upload pace for ${paceTarget.display_name}`}
          confirmLabel={paceValue === null ? 'Set interval' : `Set to ${intervalLabel(paceValue)}`}
          tone="primary"
          busy={busy}
          error={paceError}
          onCancel={() => {
            if (busy) return;
            setPaceTarget(null);
            setPaceError(null);
          }}
          onConfirm={() => {
            if (paceValue === null) {
              setPaceError(new Error(`whole seconds, from 0 to ${serverPace.max_interval_s}`));
              return;
            }
            void setPace(paceTarget, paceValue, paceReason.trim());
          }}
        >
          <p style={{ margin: 0 }}>
            This account alone waits this long between uploads of the same circuit, instead of
            the server’s {intervalLabel(serverPace.server_interval_s)}; “no wait” also lifts the
            cap across circuits for it. Nothing else changes: every bundle is still validated, and
            its installations are still bound to it. It stays until it is put back — “Default
            pace” on the row, one click.
          </p>
          {paceTarget.upload_pace.limited > 0 ? (
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--color-neutral-600)' }}>
              Refused {count(paceTarget.upload_pace.limited)}× in the last{' '}
              {serverPace.limited_window_hours} h, last {relativeTime(paceTarget.upload_pace.last_limited_at)}.
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
            <TextField
              label="interval, seconds"
              inputMode="numeric"
              placeholder={String(serverPace.server_interval_s)}
              value={paceDraft}
              onChange={(event) => setPaceDraft(event.currentTarget.value)}
              style={{ minHeight: 30, fontSize: 13, width: 110 }}
            />
            {PACE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12 }}
                disabled={busy}
                onClick={() => setPaceDraft(String(preset))}
              >
                {intervalLabel(preset)}
              </button>
            ))}
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="pace-reason">
              note{' '}
              <span style={{ color: 'var(--color-neutral-600)' }}>
                — to the audit trail, not to the account
              </span>
            </label>
            <textarea
              id="pace-reason"
              className="input"
              rows={2}
              value={paceReason}
              style={{ minHeight: 52, fontSize: 13 }}
              placeholder="why"
              onChange={(event) => setPaceReason(event.target.value)}
            />
          </div>
        </ConfirmDialog>
      ) : null}
    </Page>
  );
}
